import React, { useState } from 'react';
import { Check, ShieldCheck, Zap, Lock, Crown, Flame, Circle, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/Button';
import { FadeIn } from './ui/FadeIn';
import { CountdownTimer } from './CountdownTimer';
type PlanId = 'standard' | 'elite';
const CHECKOUT_URLS: Record<PlanId, string> = {
  standard: 'https://checkout.activadigital.store/cart/53230700364070:1',
  elite: 'https://checkout.activadigital.store/cart/53230703477030:1'
};
export const PricingSection = () => {
  const [selected, setSelected] = useState<PlanId>('elite');
  const handleBuy = () => {
    window.open(CHECKOUT_URLS[selected], '_blank', 'noopener,noreferrer');
  };
  return <section id="pricing" className="py-8 md:py-24 bg-navy-900 relative overflow-hidden" data-id="element-284">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gold-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-285" />

      <div className="container mx-auto px-4 relative z-10" data-id="element-286">
        <FadeIn data-id="element-287">
          <CountdownTimer data-id="element-288" />
        </FadeIn>

        <FadeIn className="text-center mt-4 md:mt-16 mb-4 md:mb-10" data-id="element-289">
          <h2 className="text-2xl md:text-6xl font-bebas text-white mb-1 md:mb-4" data-id="element-290">
            Elegí tu <span className="text-gold-500" data-id="element-291">Armador de Élite</span>
          </h2>
          <p className="text-xs md:text-xl text-gray-300" data-id="element-292">
            Tocá una opción para seleccionarla
          </p>
        </FadeIn>

        {/* Selectable Plan Cards */}
        <div className="max-w-5xl mx-auto space-y-2.5 md:space-y-0 md:grid md:grid-cols-2 md:gap-6 mb-4 md:mb-8" data-id="element-293">
          {/* ============ MOBILE COMPACT (Estándar) ============ */}
          <button type="button" onClick={() => setSelected('standard')} className={`md:hidden w-full text-left bg-navy-950 rounded-xl p-3 relative transition-all duration-300 border-2 ${selected === 'standard' ? 'border-gold-500 shadow-[0_0_15px_rgba(212,175,55,0.2)]' : 'border-navy-700'}`} data-id="element-294">
            <div className="flex items-center gap-3" data-id="element-295">
              {/* Radio */}
              <div className="shrink-0" data-id="element-296">
                {selected === 'standard' ? <CheckCircle2 className="w-5 h-5 text-gold-500 fill-gold-500/20" data-id="element-297" /> : <Circle className="w-5 h-5 text-navy-600" data-id="element-298" />}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-299">
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-300">
                  <span className="text-xs" data-id="element-301">📘</span>
                  <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold" data-id="element-302">
                    Estándar
                  </span>
                </div>
                <div className="font-bebas text-white text-base leading-tight" data-id="element-303">
                  ARMADOR DE ÉLITE
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5" data-id="element-304">
                  6 Tomos + 6 Bonos · Acceso de por vida
                </div>
              </div>

              {/* Price */}
              <div className="shrink-0 text-right" data-id="element-305">
                <div className="text-gray-500 line-through text-[10px] font-bold leading-none" data-id="element-306">
                  $34.990
                </div>
                <div className="font-bebas text-white text-xl leading-none mt-0.5" data-id="element-307">
                  $19.990
                </div>
                <div className="text-[9px] text-green-400 font-bold mt-0.5" data-id="element-308">
                  -43%
                </div>
              </div>
            </div>
          </button>

          {/* ============ MOBILE COMPACT (Pack Élite) ============ */}
          <button type="button" onClick={() => setSelected('elite')} className={`md:hidden w-full text-left rounded-xl p-3 relative transition-all duration-300 border-2 overflow-hidden ${selected === 'elite' ? 'border-gold-500 bg-gradient-to-br from-navy-800 to-navy-900 shadow-[0_0_20px_rgba(212,175,55,0.3)]' : 'border-gold-500/40 bg-navy-900'}`} data-id="element-309">
            {/* Ribbon */}
            <div className="absolute -top-px right-12 bg-gradient-to-r from-red-600 to-orange-500 text-white font-bold px-2 py-0.5 rounded-b-md text-[9px] flex items-center gap-1 shadow-lg whitespace-nowrap z-10" data-id="element-310">
              <Flame className="w-2.5 h-2.5 fill-yellow-300 text-yellow-300" data-id="element-311" />{' '}
              MÁS ELEGIDO
            </div>

            <div className="flex items-center gap-3 relative z-10" data-id="element-312">
              {/* Radio */}
              <div className="shrink-0" data-id="element-313">
                {selected === 'elite' ? <CheckCircle2 className="w-5 h-5 text-gold-500 fill-gold-500/30" data-id="element-314" /> : <Circle className="w-5 h-5 text-navy-600" data-id="element-315" />}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-2" data-id="element-316">
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-317">
                  <Crown className="w-3 h-3 text-gold-500" data-id="element-318" />
                  <span className="text-[10px] uppercase tracking-widest text-gold-500 font-bold" data-id="element-319">
                    Pack Completo
                  </span>
                </div>
                <div className="font-bebas text-white text-base leading-tight text-glow-gold" data-id="element-320">
                  PACK ÉLITE™
                </div>
                <div className="text-[10px] text-gold-300/80 mt-0.5" data-id="element-321">
                  Todo + Sistema Competición + Marca Personal
                </div>
              </div>

              {/* Price */}
              <div className="shrink-0 text-right pt-2" data-id="element-322">
                <div className="text-gray-400 line-through text-[10px] font-bold leading-none" data-id="element-323">
                  $43.990
                </div>
                <div className="font-bebas text-gold-500 text-xl leading-none mt-0.5 text-glow-gold" data-id="element-324">
                  $27.990
                </div>
                <div className="text-[9px] text-gold-300 font-bold mt-0.5" data-id="element-325">
                  -36%
                </div>
              </div>
            </div>
          </button>

          {/* ============ DESKTOP FULL (Estándar) ============ */}
          <FadeIn delay={0.1} className="hidden md:block" data-id="element-326">
            <button type="button" onClick={() => setSelected('standard')} className={`w-full text-left bg-navy-950 rounded-2xl p-6 h-full flex flex-col relative transition-all duration-300 border-2 ${selected === 'standard' ? 'border-gold-500 shadow-[0_0_20px_rgba(212,175,55,0.2)]' : 'border-navy-700 hover:border-navy-600'}`} data-id="element-327">
              <div className="absolute top-4 right-4" data-id="element-328">
                {selected === 'standard' ? <CheckCircle2 className="w-6 h-6 text-gold-500 fill-gold-500/20" data-id="element-329" /> : <Circle className="w-6 h-6 text-navy-600" data-id="element-330" />}
              </div>

              <div className="flex items-center gap-2 mb-3" data-id="element-331">
                <div className="w-8 h-8 rounded-full bg-navy-800 flex items-center justify-center text-lg" data-id="element-332">
                  📘
                </div>
                <div className="text-gray-400 font-bold tracking-widest uppercase text-xs" data-id="element-333">
                  ESTÁNDAR
                </div>
              </div>

              <h3 className="text-3xl font-bebas text-white leading-tight mb-1" data-id="element-334">
                ARMADOR DE ÉLITE
              </h3>
              <p className="text-gray-400 mb-4 text-sm" data-id="element-335">6 Tomos + 6 Bonos</p>

              <div className="mb-4 pb-4 border-b border-navy-800" data-id="element-336">
                <div className="flex items-baseline gap-2 flex-wrap" data-id="element-337">
                  <div className="text-4xl font-bebas text-white" data-id="element-338">
                    $19.990 <span className="text-lg text-gray-400" data-id="element-339">ARS</span>
                  </div>
                  <div className="text-gray-500 line-through text-sm font-bold" data-id="element-340">
                    $34.990
                  </div>
                </div>
                <div className="inline-block mt-1.5 text-xs text-green-400 font-bold bg-green-500/10 px-2 py-0.5 rounded-full" data-id="element-341">
                  Ahorrás $15.000
                </div>
              </div>

              <div className="space-y-2 flex-grow text-sm" data-id="element-342">
                <div className="flex items-center gap-2 text-gray-300" data-id="element-343">
                  <Check className="w-4 h-4 text-green-500 shrink-0" data-id="element-344" /> 6 Tomos
                  Completos
                </div>
                <div className="flex items-center gap-2 text-gray-300" data-id="element-345">
                  <Check className="w-4 h-4 text-green-500 shrink-0" data-id="element-346" /> 6 Bonos
                  Estratégicos
                </div>
                <div className="flex items-center gap-2 text-gray-300" data-id="element-347">
                  <Check className="w-4 h-4 text-green-500 shrink-0" data-id="element-348" /> Acceso
                  de por vida
                </div>
                <div className="flex items-center gap-2 text-gray-300" data-id="element-349">
                  <Check className="w-4 h-4 text-green-500 shrink-0" data-id="element-350" />{' '}
                  Actualizaciones gratuitas
                </div>
              </div>
            </button>
          </FadeIn>

          {/* ============ DESKTOP FULL (Pack Élite) ============ */}
          <FadeIn delay={0.15} className="hidden md:block" data-id="element-351">
            <button type="button" onClick={() => setSelected('elite')} className={`w-full text-left rounded-2xl p-6 h-full flex flex-col relative transition-all duration-300 border-2 overflow-hidden ${selected === 'elite' ? 'border-gold-500 bg-gradient-to-br from-navy-800 to-navy-900 shadow-[0_0_30px_rgba(212,175,55,0.3)]' : 'border-gold-500/40 bg-navy-900 hover:border-gold-500/70'}`} data-id="element-352">
              {selected === 'elite' && <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/15 rounded-bl-full pointer-events-none" data-id="element-353" />}

              <div className="absolute -top-px -right-px bg-gradient-to-r from-red-600 to-orange-500 text-white font-bold px-3 py-1 rounded-bl-xl text-xs flex items-center gap-1 z-10 shadow-lg whitespace-nowrap" data-id="element-354">
                <Flame className="w-3 h-3 fill-yellow-300 text-yellow-300" data-id="element-355" />{' '}
                MÁS ELEGIDO
              </div>

              <div className="absolute top-4 left-4 z-10" data-id="element-356">
                {selected === 'elite' ? <CheckCircle2 className="w-6 h-6 text-gold-500 fill-gold-500/30" data-id="element-357" /> : <Circle className="w-6 h-6 text-navy-600" data-id="element-358" />}
              </div>

              <div className="flex items-center gap-2 mb-3 ml-9 mt-1 relative z-10" data-id="element-359">
                <div className="w-8 h-8 rounded-full bg-gold-500 flex items-center justify-center shadow-lg shadow-gold-500/30" data-id="element-360">
                  <Crown className="w-4 h-4 text-navy-950" data-id="element-361" />
                </div>
                <div className="text-gold-500 font-bold tracking-widest uppercase text-xs" data-id="element-362">
                  PACK COMPLETO
                </div>
              </div>

              <h3 className="text-4xl font-bebas text-white leading-tight mb-1 relative z-10" data-id="element-363">
                PACK ÉLITE™
              </h3>
              <p className="text-gold-400 font-medium mb-4 text-sm relative z-10" data-id="element-364">
                6 Tomos + 6 Bonos + Sistema de Competición + Armador Digital
              </p>

              <div className="mb-4 pb-4 border-b border-gold-500/20 relative z-10" data-id="element-365">
                <div className="flex items-baseline gap-2 flex-wrap" data-id="element-366">
                  <div className="text-5xl font-bebas text-gold-500 text-glow-gold" data-id="element-367">
                    $27.990 <span className="text-lg text-gray-300" data-id="element-368">ARS</span>
                  </div>
                  <div className="text-gray-400 line-through text-sm font-bold" data-id="element-369">
                    $43.990
                  </div>
                </div>
                <div className="inline-block mt-1.5 text-xs text-gold-300 font-bold bg-gold-500/15 border border-gold-500/30 px-2 py-0.5 rounded-full" data-id="element-370">
                  ✨ Ahorrás $16.000
                </div>
              </div>

              <div className="space-y-2 flex-grow text-sm relative z-10" data-id="element-371">
                <div className="flex items-center gap-2 text-white font-medium" data-id="element-372">
                  <Check className="w-4 h-4 text-gold-500 shrink-0" data-id="element-373" /> Todo lo
                  del Estándar
                </div>
                <div className="flex items-center gap-2 text-white font-medium" data-id="element-374">
                  <Check className="w-4 h-4 text-gold-500 shrink-0" data-id="element-375" /> + Sistema
                  de Competición
                </div>
                <div className="flex items-center gap-2 text-white font-medium" data-id="element-376">
                  <Check className="w-4 h-4 text-gold-500 shrink-0" data-id="element-377" /> + Armador
                  Digital
                </div>
              </div>
            </button>
          </FadeIn>
        </div>

        {/* Unified CTA */}
        <FadeIn delay={0.25} className="max-w-2xl mx-auto" data-id="element-378">
          <div className="bg-navy-950/60 border border-gold-500/30 rounded-xl md:rounded-2xl p-3 md:p-5 backdrop-blur-sm" data-id="element-379">
            <Button variant="primary" size="lg" fullWidth onClick={handleBuy} className="group" data-id="element-380">
              <span className="mr-2 group-hover:scale-110 transition-transform" data-id="element-381">
                💥
              </span>
              {selected === 'elite' ? 'COMPRAR PACK ÉLITE' : 'COMPRAR ESTÁNDAR'}
            </Button>

            <div className="flex flex-wrap justify-center gap-2 md:gap-4 mt-2.5 md:mt-4 text-[10px] md:text-xs text-gray-400" data-id="element-382">
              <div className="flex items-center gap-1" data-id="element-383">
                <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-gray-400" data-id="element-384" />{' '}
                Pago seguro
              </div>
              <div className="flex items-center gap-1" data-id="element-385">
                <Zap className="w-3 h-3 md:w-3.5 md:h-3.5 text-gold-500" data-id="element-386" />{' '}
                Acceso inmediato
              </div>
              <div className="flex items-center gap-1" data-id="element-387">
                <ShieldCheck className="w-3 h-3 md:w-3.5 md:h-3.5 text-green-500" data-id="element-388" />{' '}
                Garantía 7 días
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
};